import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';

const messageOf = (e, fallback) => e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : null) || fallback;

/**
 * Org Admin > Online Store > Online payments: the store's OWN Razorpay account. The money goes straight to the merchant's Razorpay account; iBigDo only uses the keys to
 * start a payment, check it and refund it. The key secret and webhook secret are saved encrypted and never shown again (a blank box keeps what is saved).
 */
export default function RazorpaySettings({ storeSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ keyId: '', keySecret: '', webhookSecret: '', acceptOnline: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/store/razorpay');
      setData(r.data);
      setForm({ keyId: r.data.keyId || '', keySecret: '', webhookSecret: '', acceptOnline: Boolean(r.data.acceptOnline) });
    } catch (e) {
      setData(null);
    }
  }, []);
  useEffect(() => { load(); }, [load, storeSaved]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      const r = await axios.put('/api/store/razorpay', form);
      setData(r.data);
      setForm({ keyId: r.data.keyId || '', keySecret: '', webhookSecret: '', acceptOnline: Boolean(r.data.acceptOnline) });
      toast.success(r.data.acceptOnline ? 'Saved. Buyers can now pay online.' : 'Saved.');
    } catch (err) {
      toast.error(messageOf(err, 'Could not save the Razorpay settings.'));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post('/api/store/razorpay/test');
      setTestResult({ ok: true, text: `Razorpay accepted the keys (${r.data.mode} mode).` });
    } catch (err) {
      setTestResult({ ok: false, text: messageOf(err, 'Razorpay did not accept the keys.') });
    } finally {
      setTesting(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch (e) {
      toast.info(text);
    }
  };

  if (!data) return null;
  if (!data.storeSaved) {
    return <Alert variant="light" className="border mt-4" data-testid="razorpay-needs-store">Save the store settings above first. Then you can add your Razorpay account here, so buyers can pay online.</Alert>;
  }

  return (
    <Card className="mt-4" data-testid="razorpay-settings">
      <Card.Header className="d-flex align-items-center gap-2">
        <strong>Online payments with Razorpay</strong>
        {data.mode && <Badge bg={data.mode === 'live' ? 'success' : 'warning'} text={data.mode === 'live' ? undefined : 'dark'}>{data.mode === 'live' ? 'Live' : 'Test mode'}</Badge>}
        {data.acceptOnline && <Badge bg="primary">On</Badge>}
      </Card.Header>
      <Card.Body>
        <p className="small text-muted">
          Buyers pay by card, UPI, net banking or wallet in Razorpay&apos;s own window, and the money goes straight to <strong>your own Razorpay account</strong>. The order is created only
          after the payment is confirmed, so an unpaid online order never holds your stock. If the order can&apos;t be completed (the stock went while they paid), the payment is refunded at once.
        </p>
        <Form onSubmit={save}>
          <Form.Group className="mb-3">
            <Form.Label>Key id</Form.Label>
            <Form.Control value={form.keyId} onChange={set('keyId')} placeholder="rzp_test_... or rzp_live_..." autoComplete="off" data-testid="razorpay-key-id" />
            <Form.Text className="text-muted">Razorpay Dashboard &rsaquo; Account &amp; Settings &rsaquo; API Keys. Use a <em>test</em> key first to try it without real money.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Key secret {data.keySecretSet && <Badge bg="secondary">saved</Badge>}</Form.Label>
            <Form.Control type="password" value={form.keySecret} onChange={set('keySecret')} placeholder={data.keySecretSet ? 'Saved. Leave empty to keep it' : 'Paste the key secret'} autoComplete="new-password" data-testid="razorpay-key-secret" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Webhook secret {data.webhookSecretSet && <Badge bg="secondary">saved</Badge>}</Form.Label>
            <Form.Control type="password" value={form.webhookSecret} onChange={set('webhookSecret')} placeholder={data.webhookSecretSet ? 'Saved. Leave empty to keep it' : 'A password you choose (8 or more characters)'} autoComplete="new-password" data-testid="razorpay-webhook-secret" />
            <Form.Text className="text-muted">You choose this when you add the webhook below in Razorpay. Type the same one here.</Form.Text>
          </Form.Group>

          {data.webhookUrl && (
            <Alert variant="light" className="border small">
              <strong>Add this webhook in Razorpay</strong> (Account &amp; Settings &rsaquo; Webhooks &rsaquo; Add new webhook), so a payment is never lost if a buyer closes the page:
              <ol className="mb-0 mt-2">
                <li>Webhook URL: <code data-testid="webhook-url">{data.webhookUrl}</code> <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => copy(data.webhookUrl)}>Copy</Button></li>
                <li>Secret: the webhook secret above.</li>
                <li>Active events: tick {(data.webhookEvents || []).map((ev, i) => <span key={ev}>{i > 0 && ', '}<code>{ev}</code></span>)}.</li>
              </ol>
            </Alert>
          )}

          <Form.Check type="switch" id="razorpay-accept" className="mb-3" label="Let buyers pay now (Razorpay)" checked={form.acceptOnline} onChange={set('acceptOnline')} data-testid="razorpay-accept" />

          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save online payments'}</Button>
            <Button variant="outline-secondary" onClick={test} disabled={testing || !data.keySecretSet} data-testid="razorpay-test">{testing ? 'Testing...' : 'Test the keys'}</Button>
            {testResult && <span className={`small ${testResult.ok ? 'text-success' : 'text-danger'}`} data-testid="razorpay-test-result">{testResult.text}</span>}
          </div>
          <div className="small text-muted mt-2">To remove Razorpay from this store, empty the key id and save.</div>
        </Form>
      </Card.Body>
    </Card>
  );
}
